import { differenceInDays, endOfDay, isAfter } from 'date-fns';
import { clone, destroy, flow, getParent, types as t } from 'mobx-state-tree';

import { abbreviateDate } from '@utils/date';

import { sortBy, transformJob, updateMSTObject } from 'helpers';

import {
  JobAttachment,
  JobForm,
  JobIntegrations,
  JobLabel,
  JobLanguages,
  JobListView,
  JobNote,
  JobRoutes,
  JobSettings,
  JobSocial,
  JobStage,
  JobStatus,
  JobType,
  JobUserRead,
  JobVisibility,
  Meta,
} from 'models/jobs';
import { Location } from 'models/locations/Location';
import { LoadingState } from 'models/ui';
import { UserRef } from 'models/users';
import { withEnv } from 'models/withEnv';
import { WorkType } from 'models/workTypes/WorkType';

const DynamicStages = t.model('DynamicStages', {
  stage: t.number,
  autoOpenModal: t.enumeration('Modal', [
    'ASSIGN_APPLICANT',
    'HIRE_APPLICANT',
    'REJECT_APPLICANTS',
  ]),
});

export const JobApplicationFollower = t.model('JobApplicationFollower', {
  id: t.number,
  numApplications: t.number,
});

const JobBase = t
  .model('JobBase', {
    id: t.identifierNumber,
    isArchived: false,
    isPublished: false,
    metaData: t.maybeNull(Meta),
    settings: t.optional(JobSettings, {}),
    hasRewards: false,
    rewards: t.maybeNull(t.string),
    rewardsTitle: t.maybeNull(t.string),
    createdById: t.maybeNull(t.late(() => UserRef)),
    dateCreated: t.maybeNull(t.string),
    datePublished: t.maybeNull(t.string),
    dateArchived: t.maybeNull(t.string),
    dateScheduledPublish: t.maybeNull(t.string),
    deadline: t.maybeNull(t.string),
    status: t.optional(JobStatus, 'full_time'),
    jobType: t.optional(JobType, 'employee'),
    workType: t.maybeNull(WorkType),
    visibility: t.optional(JobVisibility, 'public'),
    draftStage: 0,
    ordering: 0,
    jobStages: t.array(JobStage),
    listView: t.optional(JobListView, {}),
    location: t.maybeNull(Location),
    department: t.maybeNull(t.number),
    followers: t.array(t.late(() => UserRef)),
    appFollowers: t.array(JobApplicationFollower),
    social: t.maybeNull(JobSocial),
    numApplications: t.optional(t.number, 0),
    numFilteredApplications: t.optional(t.number, 0),
    numNewApplications: t.optional(t.number, 0),
    numHiredApplications: t.maybeNull(t.number, 0),
    numRejectedApplications: t.maybeNull(t.number, 0),
    numRetractedApplications: t.maybeNull(t.number, 0),
    uuid: t.maybe(t.string),
    includeCoverLetter: false,
    requireCoverLetter: false,
    includeCv: false,
    requireCv: false,
    includePhone: false,
    requirePhone: false,
    requireSex: false,
    includeSsn: false,
    requireSsn: false,
    useMessages: false,
    form: t.maybe(JobForm),
    notes: t.array(t.late(() => JobNote)),
    attachments: t.array(t.late(() => JobAttachment)),
    uploadingAttachment: false,
    reads: t.maybeNull(t.late(() => JobUserRead)),
    labels: t.maybeNull(t.array(t.late(() => JobLabel))),

    loadingApplicationsCount: false,
    detailLoading: false,
    detailLoaded: false,
    integrationsState: LoadingState,
    // TODO: Remove once we're sure it wont break after stage change
    dynamicStages: t.array(DynamicStages),
  })
  .views(withEnv)
  .actions(() => {
    let searchQueryData = {};
    let conjunction = 'and';
    return {
      getSearchQueryData() {
        return {
          data: searchQueryData,
          conjunction,
        };
      },
      setSearchQueryData(queryData, conj) {
        searchQueryData = queryData;
        conjunction = conj;
      },
    };
  })
  .actions((self) => ({
    collapseJobStageOrder() {
      // When we add/remove/move stages, we can introduce gaps in the ordering
      // The API handles this but we want to make sure we always reflect the correct
      // ordering in the UI as well

      self.jobStages.forEach((stage, index) => {
        stage.setOrder(index + 1);
      });
    },
    moveStage: flow(function* (fromStageIndex, toStageIndex) {
      // Make a copy of the old stages in case the update fails in the API
      const oldStages = clone(self.jobStages);

      // Optimistically do the move to make it look smooth
      const stages = [...self.jobStages];
      const stageToMove = stages.splice(fromStageIndex, 1)[0];
      stages.splice(toStageIndex, 0, stageToMove);
      self.jobStages = stages;

      self.collapseJobStageOrder();

      const res = yield self.env.api.jobs.moveStage(self.id, stageToMove.id, toStageIndex + 1);
      if (res.error) {
        // Failed to move the stage, rollback the UI change and notify the user
        self.env.showError(self.env.t('job.stage.move.error'));
        self.jobStages = oldStages;
      }
    }),
    editAppFollowerCount(userId, count) {
      const index = self.appFollowers.findIndex((af) => af.id === userId);
      if (~index) {
        self.appFollowers[index].numApplications = count;
      }
    },
    setUserRead(fieldName) {
      if (self.reads && self.reads[fieldName] !== true) {
        self.reads[fieldName] = true;
        self.env.api.jobs.read(self.id, { [fieldName]: true });
      }
    },
    removeAppFollower(userId) {
      self.appFollowers = [...self.appFollowers.filter((u) => u.id !== userId)];
    },
    removeFollower(userId) {
      self.followers = [...self.followers.filter((u) => u.id !== userId)];
    },
    addFollower(user) {
      if (!self.followers.includes(user)) {
        self.followers.unshift(user);
      }
    },
    addAppFollower(userId) {
      const index = self.appFollowers.findIndex((af) => af.id === userId);
      if (index < 0) {
        self.appFollowers.unshift({ id: userId, numApplications: 0 });
      }
    },
    setNumApplications(data) {
      self.numNewApplications = data.new;
      self.numHiredApplications = data.hired;
      self.numRejectedApplications = data.rejected;
      self.numRetractedApplications = data.retracted;
      self.numApplications = data.total;
    },
    localEdit(field, value) {
      self[field] = value;
    },
    onSettingsUpdate() {
      const isPipeline = self.env.router.location.pathname.includes('/pipeline');
      if (isPipeline) {
        self.jobStages.forEach((stage) => stage.reload());
      } else {
        self.listView.onSettingsUpdate();
      }
    },
    removeLanguage: function (language) {
      self.languages = self.languages.filter((lang) => lang.language !== language);
    },
    loadFromDb: flow(function* () {
      const res = yield self.env.api.jobs.loadFromDb(self.id);
      if (!res.error) {
        updateMSTObject(self, transformJob(res.data));
      }
    }),
    reload: flow(function* () {
      let jobRes = yield self.env.api.jobs.loadJob(self.id);
      if (jobRes.error) return;
      updateMSTObject(self, transformJob(jobRes.data[0]));
    }),
    load: flow(function* () {
      if (self.detailLoaded || self.detailLoading) return;
      self.detailLoading = true;

      let jobRes = yield self.env.api.jobs.loadJob(self.id);
      if (jobRes.error) {
        return;
      }
      jobRes.data = jobRes.data[0];

      updateMSTObject(self, transformJob(jobRes.data));
      yield self.settings.load();
      self.detailLoaded = true;
      self.detailLoading = false;
    }),
    loadStages() {
      self.jobStages.forEach((stage) => stage.load(true));
    },
    resetStages() {
      self.jobStages.forEach((stage) => stage.reset());
    },
    delete() {
      return getParent(self, 2).delete(self);
    },
    removeApplication(id) {
      self.listView.removeApplication(id);
      self.numApplications -= 1;
    },
    duplicate: flow(function* () {
      const res = yield self.env.api.jobs.duplicate(self.id);
      if (!res.error) {
        self.env.jobStore.addOrUpdate(res.data);
        self.env.showSuccess(self.env.t('job.duplicate.success'), { subtitle: self.title });
      } else {
        self.env.showError(self.env.t('job.duplicate.error'), { subtitle: self.title });
      }
    }),
    unPublish: flow(function* () {
      const res = yield self.env.api.jobs.unpublish(self.id);
      if (!res.error) {
        self.isPublished = false;
        self.env.showSuccess(self.env.t('job.unpublish.success'), { subtitle: self.title });
      } else {
        self.env.showError(self.env.t('job.unpublish.error'), { subtitle: self.title });
      }
    }),
    publish: flow(function* () {
      let submit = true;

      const hasEnabled = self.languages.some((obj) => obj.isEnabled);
      if (!hasEnabled) {
        self.env.showError(self.env.t('job.create.publish.no_enabled_language'));
        return false;
      }

      self.languages.forEach((obj) => {
        if (obj.isEnabled && !obj.isComplete) {
          self.notifyIncompletes(obj.language);
          submit = false;
        }
      });

      if (!submit) return false;

      const res = yield self.env.api.jobs.publish(self.id);
      if (!res.error) {
        self.isPublished = true;
        self.isArchived = false;
        if (self.original) {
          self.original.localEdit('isPublished', true);
          self.original.localEdit('isArchived', false);
        }
        self.env.showSuccess(self.env.t('job.publish.success'), { subtitle: self.title });
        return true;
      } else {
        self.env.showError(self.env.t('job.publish.error'), { subtitle: self.title });
        return false;
      }
    }),
    moveToDrafts: flow(function* () {
      const res = yield self.env.api.jobs.moveToDrafts(self.id);
      if (!res.error) {
        self.isArchived = false;
        self.isPublished = false;
        self.env.showSuccess(self.env.t('job.drafts.success'));
      } else {
        self.env.showError(self.env.t('job.drafts.error'));
      }
    }),
    moveToArchive: flow(function* () {
      const res = yield self.env.api.jobs.moveToArchive(self.id);
      if (!res.error) {
        self.isArchived = true;
        self.isPublished = false;
        self.env.showSuccess(self.env.t('job.archive.success'));
        return true;
      } else {
        self.env.showError(self.env.t('job.archive.error'));
        return false;
      }
    }),
    renameStage: flow(function* (stageId, name) {
      const jobStage = self.jobStages.find((stage) => stage.id === stageId);
      const oldName = jobStage.name;
      jobStage.setName(name);
      const res = yield self.env.api.jobs.renameStage(self.id, stageId, name);
      if (!res.error) {
        self.env.showSuccess(self.env.t('job.renamestage.success', name));
        return true;
      } else {
        jobStage.setName(oldName);
        self.env.showError(self.env.t('job.renamestage.error', name));
        return false;
      }
    }),
    removeStage: flow(function* (stageId) {
      const res = yield self.env.api.jobs.removeStage(self.id, stageId);
      const jobStage = self.jobStages.find((stage) => stage.id === stageId);
      const name = jobStage.name;
      if (!res.error) {
        destroy(jobStage);
        self.collapseJobStageOrder();
        self.settings.removeStageSettings(stageId);
        self.env.showSuccess(self.env.t('job.removestage.success'), { subtitle: name });
        return true;
      } else {
        self.env.showError(self.env.t('job.removestage.error'), { subtitle: name });
        return false;
      }
    }),
    addStage: flow(function* () {
      const name = `Stage ${self.jobStages.length + 1}`;
      const order = self.jobStages.length + 1;
      const res = yield self.env.api.jobs.addStage(self.id, name, order);
      if (!res.error) {
        const isPipeline = self.env.router.location.pathname.includes('/pipeline');
        const newStage = {
          id: res.data.id,
          order: order,
          name: name,
          isRenaming: isPipeline,
          isNew: isPipeline,
        };
        self.jobStages.push(newStage);
        self.settings.addStageSettings(res.data.id);
        self.collapseJobStageOrder();
        self.env.showSuccess(self.env.t('job.addstage.success'));
        return true;
      } else {
        self.env.showError(self.env.t('job.addstage.error'));
        return false;
      }
    }),
    immediateUpdate: flow(function* (data) {
      const res = yield self.env.api.jobs.edit(self.id, data);
      if (!res.error) {
        updateMSTObject(self, transformJob(data));
        self.env.showSuccess(self.env.t('job.immediateupdate.success'));
        return true;
      } else {
        self.env.showError(self.env.t('job.immediateupdate.error'));
        return false;
      }
    }),
    addApplicant: flow(function* (formData) {
      formData.append('job', self.id);
      const res = yield self.env.api.applications.create(formData);
      const name = formData.get('name');
      if (!res.error) {
        // Fake it taking two extra seconds so cache can catch up
        // a little longer delay but UI will be more correct after
        yield new Promise((resolve) => setTimeout(resolve, 2000));
        self.jobStages.find((stage) => stage.isEntryStage === true).load(true);
        self.listView.load();
        self.loadApplicationsCount();
        self.loadFilteredApplicationsCount();
        self.env.showSuccess(self.env.t('job.addapplicant.success'), { subtitle: name });
      } else {
        self.env.showError(self.env.t('job.addapplicant.error'), { subtitle: name });
      }
      return res;
    }),
    updateLocal(data) {
      updateMSTObject(self, transformJob(data));
    },
    addToAppFollowCountForUser(user, count) {
      const idx = self.appFollowers.findIndex((af) => af.id === user.id);
      if (~idx) {
        self.appFollowers[idx].numApplications += count;
      } else {
        self.appFollowers.push({
          numApplications: count,
          id: user.id,
        });
      }
    },
    bulkGrantAccess: flow(function* (applicants, users) {
      const appIds = applicants.map((a) => a.id);
      const userIds = users.map((u) => u.id);
      const data = {
        jobId: self.id,
        applications: appIds,
        users: userIds,
      };
      const res = yield self.env.api.jobs.bulkGrantAccessToApplicants(data);

      if (!res.error) {
        applicants.forEach((a) => users.forEach((u) => a.setApplicationFollower(u)));
        users.forEach((u) => {
          self.addToAppFollowCountForUser(u, appIds.length);
        });
        let successMsg = self.env.t('job.bulkgrant.success', userIds.length, appIds.length);
        self.env.showSuccess(successMsg);
        return true;
      } else {
        self.env.showError(self.env.t('job.bulkgrant.error'));
        return false;
      }
    }),
    bulkRejectApplicants: flow(function* (applicants, markNotified, emailData) {
      const appIds = applicants.map((a) => a.id);
      const res = yield self.env.api.jobs.bulkRejectApplicants(
        self.id,
        appIds,
        markNotified,
        emailData
      );

      let successMsg;
      if (markNotified) {
        if (emailData) {
          successMsg = self.env.t('job.bulkreject.success.notified', appIds.length);
        } else {
          successMsg = self.env.t('job.bulkreject.success.marked', appIds.length);
        }
      } else {
        successMsg = self.env.t('job.bulkreject.success', appIds.length);
      }

      if (!res.error) {
        applicants.forEach((a) => {
          a.setRejected(true);
          a.setStatus('rejected');
          if (markNotified) {
            a.setNotifiedOfRejection(true);
          }
        });
        self.env.showSuccess(successMsg);
        return true;
      } else {
        self.env.showError(self.env.t('job.bulkreject.error'));
        return false;
      }
    }),
    bulkHireApplicants: flow(function* (applicants) {
      const appIds = applicants.map((a) => a.id);

      const res = yield self.env.api.jobs.bulkHireApplicants(self.id, appIds);

      const successMsg = self.env.t('job.bulkhire.success', appIds.length);

      if (!res.error) {
        applicants.forEach((a) => {
          a.setHired(true);
          a.setStatus('hired');
        });
        self.env.showSuccess(successMsg);
        return true;
      } else {
        self.env.showError(self.env.t('job.bulkhire.error'));
        return false;
      }
    }),
    bulkMoveToStage: flow(function* (applicants, stage) {
      const appIds = applicants.map((a) => a.id);
      const stageObj = self.jobStages.find((s) => s.id === stage);
      const stageName = stageObj.name;
      const res = yield self.env.api.jobs.bulkMoveToStage(self.id, appIds, stageObj.id);

      let successMsg = self.env.t('job.bulkmove.success', appIds.length, stageName);

      if (!res.error) {
        applicants.forEach((a) => {
          a.setStage(stage, stageName);
        });
        self.env.showSuccess(successMsg);
        return true;
      } else {
        self.env.showError(self.env.t('job.bulkmove.error'));
        return false;
      }
    }),
    bulkMessageApplicants: flow(function* (applicants, emailData) {
      const appIds = applicants.map((a) => a.id);

      const res = yield self.env.api.jobs.bulkMessageApplicants(self.id, appIds, emailData);

      const successMsg = self.env.t('job.bulkmessage.success', appIds.length);

      if (!res.error) {
        self.env.showSuccess(successMsg);
        return true;
      } else {
        self.env.showError(self.env.t('job.bulkmessage.error'));
        return false;
      }
    }),
    bulkRetractApplicants: flow(function* (applicants) {
      const appIds = applicants.map((a) => a.id);

      const res = yield self.env.api.jobs.bulkRetractApplicants(self.id, appIds);

      const successMsg = self.env.t('job.bulkretract.success', appIds.length);

      if (!res.error) {
        applicants.forEach((a) => {
          a.setRetracted(true);
          a.setStatus('retracted');
        });
        self.env.showSuccess(successMsg);
        return true;
      } else {
        self.env.showError(self.env.t('job.bulkretract.error'));
        return false;
      }
    }),
    bulkActiveApplicants: flow(function* (applicants) {
      const appIds = applicants.map((a) => a.id);

      const res = yield self.env.api.jobs.bulkActiveApplicants(self.id, appIds);

      const successMsg = self.env.t('job.bulkactive.success', appIds.length);

      if (!res.error) {
        applicants.forEach((a) => {
          a.setHired(false);
          a.setRetracted(false);
          a.setRejected(false);
          a.setNotifiedOfRejection(false);
          a.setStatus('active');
        });
        self.env.showSuccess(successMsg);
        return true;
      } else {
        self.env.showError(self.env.t('job.bulkactive.error'));
        return false;
      }
    }),
    bulkDeleteApplicants: flow(function* (applicants) {
      const appIds = applicants.map((a) => a.id);

      const res = yield self.env.api.jobs.bulkDeleteApplicants(self.id, appIds);

      const successMsg = self.env.t('job.bulkdelete.success', appIds.length);

      if (!res.error) {
        // Need to wait before loading becaues elasticsearch is not updated immediately
        setTimeout(() => {
          self.listView.clearSelected();
          self.listView.load();
          self.loadApplicationsCount();
          self.loadFilteredApplicationsCount();
        }, 1000);
        self.env.showSuccess(successMsg);
        return true;
      } else {
        self.env.showError(self.env.t('job.bulkdelete.error'));
        return false;
      }
    }),
    exportApplicants: flow(function* (fields, exportType) {
      const res = yield self.env.api.jobs.exportApplicants(self.id, fields, exportType);
      if (!res.error) {
        return true;
      } else {
        return false;
      }
    }),
    removeNote: flow(function* (note) {
      const res = yield self.env.api.jobs.removeNote(note.id);
      if (!res.error) {
        self.env.showSuccess(self.env.t('job.info.notes.remove.success'));
        destroy(note);
        return true;
      } else {
        self.env.showError(self.env.t('job.info.notes.remove.error'));
        return false;
      }
    }),
    addNote: flow(function* (message, isAdminNote) {
      const res = yield self.env.api.jobs.addNote({
        text: message,
        job: self.id,
        isAdminNote: isAdminNote,
      });
      if (!res.error) {
        self.env.showSuccess(self.env.t('job.info.notes.add.success'));
        self.notes.unshift({ ...res.data });
        return true;
      } else {
        self.env.showError(self.env.t('job.info.notes.add.error'));
        return false;
      }
    }),
    loadJobApplicationsCount: flow(function* () {
      self.loadingApplicationsCount = true;
      const { data, conjunction } = self.getSearchQueryData();
      if (Object.keys(data).length === 0) {
        const res = yield self.env.api.jobs.loadApplicationsCount(self.id);
        if (!res.error) {
          self.numFilteredApplications = res.data.count;
          self.numApplications = res.data.count;
        }
        self.loadingApplicationsCount = false;
        return;
      }

      const [filteredRes, res] = yield Promise.all([
        self.env.api.jobs.loadApplicationsCount(self.id, conjunction, data),
        self.env.api.jobs.loadApplicationsCount(self.id),
      ]);
      if (!filteredRes.error) self.numFilteredApplications = filteredRes.data.count;
      if (!res.error) self.numApplications = res.data.count;
      self.loadingApplicationsCount = false;
    }),
    loadFilteredApplicationsCount: flow(function* () {
      const { data, conjunction } = self.getSearchQueryData();
      const res = yield self.env.api.jobs.loadApplicationsCount(self.id, conjunction, data);
      if (!res.error) {
        self.numFilteredApplications = res.data.count;
      }
    }),
    loadApplicationsCount: flow(function* () {
      const res = yield self.env.api.jobs.loadApplicationsCount(self.id);
      if (!res.error) {
        self.numApplications = res.data.count;
      }
    }),
    addAttachment: flow(function* (data) {
      self.uploadingAttachment = true;
      const res = yield self.env.api.jobs.addAttachment(self.id, data);
      if (res.status === 201) {
        self.attachments.unshift({
          attachment: res.data.attachment,
          attachmentOriginal: res.data.attachmentOriginal,
          fileName: res.data.fileName,
          id: res.data.id,
          user: res.data.userId,
          date: res.data.date,
        });
        self.env.showSuccess(self.env.t('job.info.files.upload.success'));
      } else {
        self.env.showError(self.env.t('job.info.files.upload.error'));
      }
      self.uploadingAttachment = false;
    }),
    removeAttachment: flow(function* (attachment) {
      const res = yield self.env.api.jobs.removeAttachment(self.id, attachment.id);
      if (res.status === 204) {
        destroy(attachment);
        self.env.showSuccess(self.env.t('job.info.files.remove.success'));
      } else {
        self.env.showError(self.env.t('job.info.files.remove.error'));
      }
      return true;
    }),
    departmentDisplay(language) {
      const departments = self.env.current.info.departments ?? [];
      const department = departments.find((department) => department.id === self.department);

      const translation =
        department?.translations.find((translation) => translation.language === language) ??
        department?.translations.find(Boolean); // Fallback to the first one if no translation is found for the language

      if (!translation) {
        return self.env.t('job.nodepartment');
      }
      return translation.title;
    },
    locationDisplay(language) {
      const locations = self.env.current.info.locations;
      if (!self.location) {
        return self.env.t('job.nolocation');
      }
      const currentLocation = locations.find((location) => location.id === self.location.id);
      if (!currentLocation) {
        return self.env.t('job.nolocation');
      }
      const translation =
        currentLocation.translations.find((translation) => translation.language === language) ??
        currentLocation.translations.find(Boolean); // Fallback to the first one if no translation is found for the language
      if (!translation) {
        return self.env.t('job.nolocation');
      }
      return translation.title;
    },
    workTypeDisplay(language) {
      const worktypes = self.env.current.info.workTypes;
      if (!self.workType) {
        return '';
      }
      const currentType = worktypes.find((worktype) => worktype.id === self.workType.id) ?? {
        translations: [],
      };
      const translation =
        currentType.translations?.find((translation) => translation.language === language) ??
        currentType.translations?.find(Boolean); // Fallback to the first one if no translation is found for the language

      if (!translation) {
        return '';
      }
      return translation.title;
    },
  }))
  .views((self) => ({
    get state() {
      if (self.isArchived) return 'archived';
      else if (self.isExpired) return 'expired';
      else if (self.isPublished) return 'published';
      else return 'draft';
    },
    get numActiveApplications() {
      return (
        self.numApplications -
        self.numRejectedApplications -
        self.numHiredApplications -
        self.numRetractedApplications
      );
    },
    get applications() {
      return self.env.applicationStore.list.filter((a) => a.job === self);
    },
    get activeFollowers() {
      return sortBy(
        self.followers.filter((user) => user.isActive && !user.isSystemUser),
        'name'
      );
    },
    get activeAppFollowers() {
      return sortBy(
        self.appFollowers.map((af) => self.env.userStore.items.get(af.id)),
        'name'
      );
    },
    get combinedFollowers() {
      const appFollowerUsers = self.appFollowers.map((af) => self.env.userStore.items.get(af.id));
      const combined = self.followers
        .concat(appFollowerUsers)
        .filter((user) => user.isActive && !user.isSystemUser);
      return sortBy(combined, 'name');
    },
    get applicantJobStages() {
      return self.jobStages.map((stage) => ({ value: stage.id, label: stage.name }));
    },
    get enabledLanguages() {
      return self.languages.filter((lang) => lang.isEnabled);
    },
    get visibilityTypes() {
      return [
        { value: 'public', label: self.env.t('job.visibility.public') },
        { value: 'private', label: self.env.t('job.visibility.private') },
      ];
    },
    get statusTypes() {
      return [
        { value: 'full_time', label: self.env.t('job.status.full_time') },
        { value: 'part_time', label: self.env.t('job.status.part_time') },
        { value: 'per_diem', label: self.env.t('job.status.per_diem') },
        { value: 'not_specified', label: self.env.t('job.status.not_specified') },
      ];
    },
    get jobTypes() {
      return [
        { value: 'employee', label: self.env.t('job.types.employee') },
        { value: 'temporary', label: self.env.t('job.types.temporary') },
        { value: 'intern', label: self.env.t('job.types.intern') },
        { value: 'seasonal', label: self.env.t('job.types.seasonal') },
        { value: 'not_specified', label: self.env.t('job.types.not_specified') },
      ];
    },
    get jobTypeDisplay() {
      if (self.jobType === 'not_specified') {
        return '';
      }
      return self.env.t(`job.type.${self.jobType}`);
    },
    get isExpired() {
      if (!self.deadline) return false;
      return isAfter(new Date(), endOfDay(new Date(self.deadline)));
    },
    get daysOpen() {
      return differenceInDays(new Date(), endOfDay(new Date(self.datePublished)));
    },
    get daysUntilDeadline() {
      return differenceInDays(endOfDay(new Date(self.deadline)), new Date());
    },
    get defaultLanguageData() {
      const defaultLang = self.env.current.info.defaultLanguage;
      const def = self.languages.find((obj) => obj.language === defaultLang);
      if (!def || !def.isEnabled) {
        if (self.languages.length > 0) {
          return self.languages.find((obj) => obj.isEnabled) || self.languages[0];
        }
        return null;
      }
      return def;
    },
    get title() {
      return self.langTitle || self.env.t('job.notitle');
    },
    get langTitle() {
      return self.defaultLanguageData ? self.defaultLanguageData.title : '';
    },
    get deadlineDisplay() {
      return self.deadline
        ? abbreviateDate(self.deadline, self.env.lang)
        : self.env.t('job.nodeadline');
    },
    get shortDeadlineDisplay() {
      return self.deadline
        ? abbreviateDate(self.deadline, self.env.lang)
        : self.env.t('job.nodeadline');
    },
    get datePublishedDisplay() {
      return self.datePublished
        ? self.env.t('job.datepublished', abbreviateDate(self.datePublished, self.env.lang))
        : self.isPublished
        ? self.env.t('job.unknowndatepublished')
        : self.env.t('job.nodatepublished');
    },
    get datePublishedDisplayWithoutPrefix() {
      return self.datePublished
        ? abbreviateDate(self.datePublished, self.env.lang)
        : self.isPublished
        ? self.env.t('job.unknowndatepublished')
        : self.env.t('job.nodatepublished');
    },
    get dateCreatedDisplay() {
      return self.dateCreated
        ? self.env.t('job.datecreated', abbreviateDate(self.dateCreated, self.env.lang))
        : self.env.t('job.nodatecreated');
    },
    get dateExpiredDisplay() {
      return self.deadline
        ? self.env.t('job.dateexpired', abbreviateDate(self.deadline, self.env.lang))
        : self.env.t('job.nodateexpired');
    },
    get dateArchivedDisplay() {
      return self.dateArchived
        ? self.env.t('job.datearchived', abbreviateDate(self.dateArchived, self.env.lang))
        : self.env.t('job.nodatearchived');
    },
    get dateScheduledPublishDisplay() {
      return self.dateScheduledPublish && self.state === 'draft'
        ? self.env.t(
            'job.datescheduledpublish',
            abbreviateDate(self.dateScheduledPublish, self.env.lang)
          )
        : '';
    },
    get labelsDisplay() {
      const labels = self.labels ?? [];
      return (self.env.current.info.jobLabels ?? []).filter((label) =>
        labels.some((l) => l.id === label.id)
      );
    },
  }));

export const Job = t.compose(JobBase, JobLanguages, JobIntegrations, JobRoutes).named('Job');
